import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { companiesApi } from '../../api/services';
import { setCompanies, NO_COMPANY } from '../../store';
import { useAlerts } from '../../custom/Alerts';
import { fmtDate } from '../../utils/dates';
import CompanyForm from './CompanyForm';
import PageHeader, { tableEmpty } from '../common/PageHeader';
import TableSearch, { filtriraj, useFitRows, usePage, PAGINATOR } from '../common/TableSearch';

export default function Companies({ active }) {
    const dispatch = useDispatch();
    const alerts = useAlerts();
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [editId, setEditId] = useState(undefined); // undefined = zatvoreno, 0 = nova
    const [search, setSearch] = useState('');
    const tableRef = useRef(null);
    const filtered = useMemo(() => filtriraj(rows, search, (c) => [c.tin, c.name, c.address, c.country, c.city, fmtDate(c.contractIssueDate), fmtDate(c.contractExpiryDate)]), [rows, search]);
    const perPage = useFitRows(tableRef, active, { dep: rows.length > 0 });
    const [first, setFirst] = usePage(filtered.length, perPage, search);

    const refresh = useCallback(async () => {
        setLoading(true);
        try {
            const list = await companiesApi.all();
            setRows(list);
            dispatch(setCompanies([NO_COMPANY, ...list]));
        } catch { /* toast */ }
        setLoading(false);
    }, [dispatch]);

    useEffect(() => { refresh(); }, [refresh]);

    const remove = async (c) => {
        if (!(await alerts.confirm('Potvrdi', `Da li ste sigurni da želite da obrišete kompaniju '${c.name}'?`, { severity: 'danger' }))) return;
        try {
            await companiesApi.remove(c.id);
            await refresh();
        } catch { /* toast */ }
    };

    return (
        <>
            <PageHeader title="Pregled kompanija" subtitle="Kompanije-klijenti i podaci o ugovoru.">
                <Button icon="pi pi-refresh" outlined severity="secondary" tooltip="Osveži" tooltipOptions={{ position: 'bottom' }} aria-label="Osveži" onClick={refresh} />
                <Button label="Dodaj kompaniju" icon="pi pi-plus" onClick={() => setEditId(0)} />
            </PageHeader>
            <div className="panel">
                <TableSearch value={search} onChange={setSearch} placeholder="Pretraga (PIB, naziv, adresa, država...)" label="Pretraga kompanija"
                    ukupno={rows.length} pronadjeno={filtered.length} jedinica="kompanija" />
                <div ref={tableRef}>
                <DataTable value={filtered} loading={loading} {...PAGINATOR} rows={perPage} first={first} onPage={(e) => setFirst(e.first)} dataKey="id" size="small" stripedRows removableSort
                    emptyMessage={tableEmpty(search.trim() ? 'Nijedna kompanija ne odgovara pretrazi' : 'Nijedna kompanija nije pronađena', 'pi pi-building')} onRowDoubleClick={(e) => setEditId(e.data.id)}
                    rowClassName={() => 'row-clickable'}>
                    <Column header="" headerClassName="actions" bodyClassName="actions" style={{ width: '5.5rem' }} body={(c) => (
                        <span>
                            <Button icon="pi pi-pencil" rounded text severity="secondary" onClick={() => setEditId(c.id)} aria-label="Izmeni kompaniju" tooltip="Izmeni" />
                            <Button icon="pi pi-trash" rounded text severity="danger" onClick={() => remove(c)} aria-label="Obriši kompaniju" tooltip="Obriši" />
                        </span>
                    )} />
                    <Column field="tin" header="PIB" sortable style={{ minWidth: '8rem' }} />
                    <Column field="name" header="Naziv" sortable style={{ minWidth: '12rem' }} />
                    <Column field="address" header="Adresa" sortable style={{ minWidth: '12rem' }} />
                    <Column field="country" header="Država" sortable />
                    <Column field="contractIssueDate" header="Datum sklapanja ugovora" sortable body={(c) => fmtDate(c.contractIssueDate)} />
                    <Column field="contractExpiryDate" header="Datum isteka ugovora" sortable body={(c) => fmtDate(c.contractExpiryDate)} />
                </DataTable>
                </div>
                <p className="panel-note"><i className="pi pi-info-circle" aria-hidden="true" />Dvoklik na red otvara izmenu kompanije.</p>
            </div>
            {editId !== undefined && (
                <CompanyForm id={editId} onClose={() => setEditId(undefined)} onSaved={refresh} />
            )}
        </>
    );
}
